import { BaseEntite } from '../../../../../common/src/entities';
import React, { ReactElement, ReactNode } from 'react';
import { useAlertDialog } from '../../../Utils/dialogContext';
import api, { useCreateEntite } from '../../../Utils/api';
import { FormikHelpers } from 'formik';
import FormulaireEntite, { BasePropsDeFormulaireEntite } from './FormulaireEntite';
import { AxiosError, AxiosResponse } from 'axios';

export type PropsDeCreationEntite<T extends BaseEntite> = BasePropsDeFormulaireEntite<T> & {
    texteEntete?: ReactNode | ((t: T) => ReactNode);
    valeursInitiales?: Partial<T>;
    aussiEnvoyerCreated_at?: boolean;
    properties_to_reassign?: object;
};

const MODIFIER_ERREUR_MESSAGE = 'Erreur dans la mise à jour des données:';
const CREER_ERREUR_MESSAGE = "Erreur dans la creation de l'entite:";
const FORMULAIRE_ENTETE = 'Création';

/** Soit properties_to_reassign un dictionnaire de { "properties1":"properties2" }
 * Cette fonction permet de remplacer la valeur gardée dans le membre "properties2" (value.properties2)
 * par la valeur provenant du membre "properties1" (value.properties1) seulement lorsque les conditions sont respectées.
 * Au final, ça permet de faire value.properties2 = value.properties1  */
function modificationAdditionelle<T>(valeur: T, properties_to_reassign: object) {
    let valeurTemp: any = valeur;
    for (let key in properties_to_reassign) {
        const properties_to_be_replaced = properties_to_reassign[key];
        const properties_replacement = key;

        if (
            valeurTemp[properties_replacement] &&
            valeurTemp[properties_replacement].length &&
            valeurTemp[properties_replacement].length != 0
        ) {
            valeurTemp[properties_to_be_replaced] = valeurTemp[properties_replacement][0].id;
        } else if (valeurTemp[properties_replacement] && valeurTemp[properties_replacement].id) {
            valeurTemp[properties_to_be_replaced] = valeurTemp[properties_replacement].id;
        } else {
            valeurTemp[properties_to_be_replaced] = null;
        }
        valeurTemp[properties_replacement] = undefined;
    }

    return valeurTemp;
}

/**
 * Formulaire correspondant à la création d'une nouvelle entité
 * lorsqu'on appuie sur + Ajouter un {entite} dans le dashboard
 *
 * @param props variables du component React parent
 * @returns html du formulaire
 */
export function FormulaireCreationEntite<T extends BaseEntite>(props: PropsDeCreationEntite<T>): ReactElement {
    const {
        entite,
        valeursInitiales,
        noSave,
        aussiEnvoyerCreated_at,
        properties_to_reassign,
        nettoyer = (_valeurs: T) => null,
    } = props;
    const bsAlert = useAlertDialog();
    const [creerEntite] = useCreateEntite(entite);
    /**
     * On desire rester sur la page de creation d'entite après sauvegarde. Permet de switch
     * entre creer une nouvelle entite et modifier l'entité courante.
     *
     * Pour modifier une entite, on a besoin :
     *   - du id genere par creerEntite() -> sauvegardé par variable valeursInitiales
     *   - certaines entite ont aussi besoin du created_at genere par creerEntite -> determine par aussiEnvoyerCreated_at
     *
     * @param valeurs entite retournee par le formulaire rempli
     * @param formikHelper Formik state helpers
     * @returns entite retournee par la BD (inclu le ID assigne)
     */
    async function gererSoumettreFormulaire(
        valeurs: T,
        formikHelper: FormikHelpers<T>,
        intitalValues?: T,
    ): Promise<T | null> {
        nettoyer(valeurs);
        if (valeursInitiales?.id !== undefined) {
            valeurs.id = valeursInitiales!.id;
            if (aussiEnvoyerCreated_at !== undefined && aussiEnvoyerCreated_at)
                valeurs!['created_at'] = valeursInitiales!['created_at'];
            return modifierFormulaire(valeurs, formikHelper);
        } else {
            return soumettreFormulaire(valeurs, formikHelper);
        }
    }

    /**
     * Modifie une entrée existante dans la BD à partir des infos
     * du formulaire qui est encore en mode création
     *
     * @param valeurs entite retournee par le formulaire rempli.
     * @returns entite retournee par la BD (inclu le ID assigne).
     */
    async function modifierFormulaire(valeurs: T, formikHelper: FormikHelpers<T>): Promise<T> {
        return api
            .patch(`/${entite.key}/${valeurs.id}`, valeurs)
            .then((res: AxiosResponse) => res.data)
            .catch((err: AxiosError) => bsAlert(`${MODIFIER_ERREUR_MESSAGE} ${err.message + ' modifier'}  `, 'danger'));
    }

    /**
     * Crée une nouvelle entité dans la BD à partir des infos
     * du formulaire qui est en mode création. État initial du
     * forumulaire
     *
     * @param valeurs entite retournee par le formulaire rempli.
     * @param formikHelper Formik state helpers
     * @returns entite retournee par la BD (inclu le ID assigne).
     */
    async function soumettreFormulaire(valeurs: T, formikHelper: FormikHelpers<T>): Promise<T | null> {
        let result: T | null = null;
        valeurs = properties_to_reassign ? modificationAdditionelle<T>(valeurs, properties_to_reassign) : valeurs;
        try {
            result = noSave ? valeurs : (await creerEntite(valeurs)) ?? null;
        } catch (error) {
            bsAlert(`${CREER_ERREUR_MESSAGE} ${(error as AxiosError).message}`, 'danger');
        }
        formikHelper.setSubmitting(false);
        return result;
    }

    return (
        <>
            <FormulaireEntite
                texteEntete={FORMULAIRE_ENTETE}
                {...props}
                soumettreFormulaire={gererSoumettreFormulaire}
                valeursInitiales={{ ...new entite(), ...valeursInitiales }}
                sauvegardeEnToutTemps
                actions={props.actions}
            />
        </>
    );
}
